import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  LogIn,
  Shield,
  Users,
  Package,
  ShoppingBag,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useLoginMutation } from '../app/api/authApi';
import { useAppSettings } from '../context/AppSettingsContext';
import { buildImageUrl } from '../utils/imageUrl';
import './Login.css'; // ✅ CSS import

const Login = () => {
  const navigate = useNavigate();

  // ✅ Settings context (dynamic logo/name)
  const { settings, imageBaseUrl, loading: settingsLoading } = useAppSettings();

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');

  // ✅ RTK Query Mutation
  const [login, { isLoading: loading }] = useLoginMutation();

  // ✅ Dynamic store info
  const storeName = settings?.general?.storeName || 'Hayat Herbal';
  const storeLogo = settings?.general?.storeLogo;
  const storeTagline = settings?.general?.storeTagline;

  const logoUrl = buildImageUrl(storeLogo, imageBaseUrl);

  // ✅ 3D Mouse Parallax Effect
  useEffect(() => {
    const scene = document.querySelector('.scene');
    if (!scene) return;

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      scene.style.transform = `rotateY(${x * 6}deg) rotateX(${-y * 6}deg)`;
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (loginError) setLoginError('');
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoginError('');

    try {
      const response = await login({
        email: formData.email,
        password: formData.password,
      }).unwrap();

      // ✅ Store token + user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.admin));
      if (formData.rememberMe) localStorage.setItem('rememberMe', 'true');

      toast.success('✅ Login successful! Welcome back!');

      setTimeout(() => navigate('/'), 1000);
    } catch (error) {
      console.error('Login error:', error);

      const errorMessage =
        error?.data?.message ||
        error?.error ||
        'Invalid email or password';

      setLoginError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="login-page">
      {/* ✅ 3D Animated Background */}
      <div className="login-background">
        <div className="login-overlay" />

        {/* 3D Moving Cubes Scene */}
        <div className="scene">
          <div className="cube-wrap cube-wrap-1">
            <div className="cube">
              <div className="face front" />
              <div className="face back" />
              <div className="face right" />
              <div className="face left" />
              <div className="face top" />
              <div className="face bottom" />
            </div>
          </div>
          <div className="cube-wrap cube-wrap-2">
            <div className="cube">
              <div className="face front" />
              <div className="face back" />
              <div className="face right" />
              <div className="face left" />
              <div className="face top" />
              <div className="face bottom" />
            </div>
          </div>
          <div className="cube-wrap cube-wrap-3">
            <div className="cube">
              <div className="face front" />
              <div className="face back" />
              <div className="face right" />
              <div className="face left" />
              <div className="face top" />
              <div className="face bottom" />
            </div>
          </div>
          <div className="cube-wrap cube-wrap-4">
            <div className="cube">
              <div className="face front" />
              <div className="face back" />
              <div className="face right" />
              <div className="face left" />
              <div className="face top" />
              <div className="face bottom" />
            </div>
          </div>
          <div className="cube-wrap cube-wrap-5">
            <div className="cube">
              <div className="face front" />
              <div className="face back" />
              <div className="face right" />
              <div className="face left" />
              <div className="face top" />
              <div className="face bottom" />
            </div>
          </div>
          <div className="cube-wrap cube-wrap-6">
            <div className="cube">
              <div className="face front" />
              <div className="face back" />
              <div className="face right" />
              <div className="face left" />
              <div className="face top" />
              <div className="face bottom" />
            </div>
          </div>

          {/* Floating Rings */}
          <div className="ring ring-1" />
          <div className="ring ring-2" />
          <div className="ring ring-3" />

          {/* Particles */}
          <div className="particles">
            {Array.from({ length: 30 }).map((_, i) => (
              <span key={i} className={`dot dot-${i + 1}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Floating Stats */}
      <div className="login-stats-floating">
        <div className="floating-stat">
          <Users size={20} />
          <span>158+ Customers</span>
        </div>
        <div className="floating-stat">
          <Package size={20} />
          <span>326+ Products</span>
        </div>
        <div className="floating-stat">
          <ShoppingBag size={20} />
          <span>258+ Orders</span>
        </div>
        <div className="floating-stat">
          <TrendingUp size={20} />
          <span>₹1,28,450+ Revenue</span>
        </div>
      </div>

      {/* Container */}
      <div className="login-container">
        <div className="login-box">
          {/* ✅ Dynamic Brand */}
          <div className="login-brand">
            <div className="login-logo">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={storeName}
                  className="login-logo-img"
                />
              ) : (
                <span className="logo-icon">🌿</span>
              )}
            </div>
            <h1 className="login-store-name">{storeName}</h1>
            <p className="login-subtitle">
              {storeTagline || 'Admin Panel Login'}
            </p>
          </div>

          {/* Error */}
          {loginError && (
            <div className="login-error">
              <AlertCircle size={18} />
              {loginError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">
                Email Address <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <Mail size={18} className="input-icon" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-control ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  disabled={loading}
                />
                {formData.email && !errors.email && (
                  <CheckCircle size={16} className="input-valid" />
                )}
              </div>
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Password <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-control ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password}</div>}
            </div>

            <div className="login-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span>Remember me</span>
              </label>
              <button type="button" className="forgot-password" disabled={loading}>
                Forgot Password?
              </button>
            </div>

            <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <p className="footer-text">
              <Shield size={14} />
              Secure Login • SSL Encrypted
            </p>
            <p className="footer-version">v2.0.0</p>
          </div>
        </div>

        {/* Features Side */}
        <div className="login-features">
          <div className="features-content">
            <h2>Welcome Back!</h2>
            <p>Manage your herbal store with ease</p>
            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">📊</div>
                <div>
                  <h4>Dashboard Overview</h4>
                  <p>Real-time analytics and insights</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📦</div>
                <div>
                  <h4>Product Management</h4>
                  <p>Manage your inventory efficiently</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🛒</div>
                <div>
                  <h4>Order Tracking</h4>
                  <p>Track and manage all orders</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">👥</div>
                <div>
                  <h4>Customer Relations</h4>
                  <p>Build lasting customer relationships</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;